import React, { useState } from 'react';
import {
  LineChart,
  Line, XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { TrendingUp, TrendingDown, Target, Users } from 'lucide-react';
import { IGeneralMetrics } from '../../../models/generalMetrics/IGeneralMetrics';
import { formatCurrencyAmount, formatPercentageValue } from '../../../utils/miscFunctions';
import { BanknotesIcon } from '@heroicons/react/24/solid';
import { generateBusinessKeyMetrics } from '../../../data/mockSuperAdminData';

interface KeyBusinessMetricsPanelProps {
  metrics: IGeneralMetrics;
  country?: string;
}

const KeyBusinessMetricsPanel: React.FC<KeyBusinessMetricsPanelProps> = ({ metrics, country = 'gh' }) => {
  const [activeMetricType, setActiveMetricType] = useState<'growth' | 'financial' | 'customer'>('growth');
  const [selectedHistoryMetric, setSelectedHistoryMetric] = useState<string>('arpu');

  const businessKeyMetrics = generateBusinessKeyMetrics();



  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="p-3 bg-white shadow-lg rounded-md border border-gray-100">
          <p className="font-medium text-sm text-gray-900">{label}</p>
          <div>
            {payload.map((entry: any, index: number) => {
              const value = entry.dataKey === 'arpu' || entry.dataKey === 'cac' || entry.dataKey === 'ltv'
                ? formatCurrencyAmount(entry.value)
                : entry.dataKey === 'margin' || entry.dataKey === 'churn'
                  ? formatPercentageValue(entry.value)
                  : entry.value;

              return (
                <p key={`metric-${index}`} className="text-sm" style={{ color: entry.color }}>
                  {entry.name}: {value}
                </p>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  // Calculate CAC:LTV ratio
  const cacLtvRatio = metrics?.cac > 0
    ? (metrics?.ltv / metrics?.cac)?.toFixed(1)
    : '0.0';

  // Filter metrics based on active type
  const getActiveMetrics = () => {
    switch (activeMetricType) {
      case 'growth':
        return [
          {
            title: 'Revenue Growth Rate',
            value: formatPercentageValue(metrics?.rgr),
            icon: <TrendingUp size={20} />,
            color: metrics?.rgr > 10 ? 'green' : 'blue',
            className: `bg-green-50 text-green-700 border-green-200 
                      dark:bg-green-900/20 dark:text-green-100 dark:border-green-800/50`,
            change: `${metrics?.rgr > 10 ? '+' : ''}${metrics?.rgr - 10}% vs target`
          },
          {
            title: 'North Star Metric',
            value: metrics?.nsm,
            icon: <Target size={20} />,
            color: 'indigo',
            className: `bg-indigo-50 text-indigo-700 border-indigo-200 
                      dark:bg-indigo-900/20 dark:text-indigo-100 dark:border-indigo-800/50`
          },
          {
            title: 'Gross Merchandise Value',
            value: formatCurrencyAmount(metrics?.gmv || 0),
            icon: <BanknotesIcon className="w-5 h-5" />,
            color: 'blue',
            className: `bg-blue-50 text-blue-700 border-blue-200 
                      dark:bg-blue-900/20 dark:text-blue-100 dark:border-blue-800/50`,
            change: 'YTD'
          },
          {
            title: 'Monthly Recurring Revenue',
            value: formatCurrencyAmount(metrics?.mmr || 0),
            icon: <BanknotesIcon className="w-5 h-5" />,
            color: 'green',
            className: `bg-green-50 text-green-700 border-green-200 
                      dark:bg-green-900/20 dark:text-green-100 dark:border-green-800/50`,
            change: 'Current month'
          }
        ];
      case 'financial':
        return [
          {
            title: 'Total Revenue',
            value: formatCurrencyAmount(metrics?.totalRevenue || 0),
            icon: <BanknotesIcon className="w-5 h-5" />,
            color: 'indigo',
            className: `bg-indigo-50 text-indigo-700 border-indigo-200 
                      dark:bg-indigo-900/20 dark:text-indigo-100 dark:border-indigo-800/50`,
            change: 'YTD'
          },
          {
            title: 'Annual Recurring Revenue',
            value: formatCurrencyAmount(metrics?.arr || 0),
            icon: <BanknotesIcon className="w-5 h-5" />,
            color: 'blue',
            className: `bg-blue-50 text-blue-700 border-blue-200 
                      dark:bg-blue-900/20 dark:text-blue-100 dark:border-blue-800/50`,
            change: 'Projected'
          },
          {
            title: 'Gross Margin',
            value: formatPercentageValue(metrics?.grossMargin),
            icon: <TrendingUp className="w-5 h-5" />,
            color: metrics?.grossMargin > 65 ? 'green' : 'yellow',
            className: metrics?.grossMargin > 65
              ? `bg-green-50 text-green-700 border-green-200 
                dark:bg-green-900/20 dark:text-green-100 dark:border-green-800/50`
              : `bg-yellow-50 text-yellow-700 border-yellow-200 
                dark:bg-yellow-900/20 dark:text-yellow-100 dark:border-yellow-800/50`,
            change: `${metrics?.grossMargin > 65 ? 'Healthy' : 'Needs improvement'}`
          },
          {
            title: 'Monthly Recurring Revenue',
            value: formatCurrencyAmount(metrics?.mmr || 0),
            icon: <BanknotesIcon className="w-5 h-5" />,
            color: 'green',
            className: `bg-green-50 text-green-700 border-green-200 
                      dark:bg-green-900/20 dark:text-green-100 dark:border-green-800/50`,
            change: 'Current month'
          }
        ];
      case 'customer':
        return [
          {
            title: 'Average Revenue Per User',
            value: formatCurrencyAmount(metrics?.arpc || 0),
            icon: <Users className="w-5 h-5" />,
            color: 'indigo',
            className: `bg-indigo-50 text-indigo-700 border-indigo-200 
                      dark:bg-indigo-900/20 dark:text-indigo-100 dark:border-indigo-800/50`,
            change: 'Per month'
          },
          {
            title: 'Customer Acquisition Cost',
            value: formatCurrencyAmount(metrics?.cac || 0),
            icon: <BanknotesIcon className="w-5 h-5" />,
            color: 'blue',
            className: `bg-blue-50 text-blue-700 border-blue-200 
                      dark:bg-blue-900/20 dark:text-blue-100 dark:border-blue-800/50`,
            change: 'Per customer'
          },
          {
            title: 'Customer Lifetime Value',
            value: formatCurrencyAmount(metrics?.ltv || 0),
            icon: <Users className="w-5 h-5" />,
            color: 'green',
            className: `bg-green-50 text-green-700 border-green-200 
                      dark:bg-green-900/20 dark:text-green-100 dark:border-green-800/50`,
            change: `${cacLtvRatio}x CAC ratio`
          },
          {
            title: 'Churn Rate',
            value: `${metrics?.churn.toFixed(2)}%`,
            icon: <TrendingDown size={20} />,
            color: metrics?.churn < 5 ? 'green' : 'red',
            className: metrics?.churn < 5
              ? `bg-green-50 text-green-700 border-green-200 
                dark:bg-green-900/20 dark:text-green-100 dark:border-green-800/50`
              : `bg-red-50 text-red-700 border-red-200 
                dark:bg-red-900/20 dark:text-red-100 dark:border-red-800/50`,
            change: `${metrics?.churn < 5 ? 'Healthy' : 'Needs improvement'}`
          }
        ];
    }
  };

  const activeMetrics = getActiveMetrics();

  // Map history metric to display name
  const metricDisplayNames: Record<string, string> = {
    arpu: 'Average Revenue Per User',
    cac: 'Customer Acquisition Cost',
    ltv: 'Lifetime Value',
    churn: 'Churn Rate',
    margin: 'Gross Margin'
  };

  // Format data point for chart based on metric type
  const formatValue = (value: number, metricType: string): string => {
    if (metricType === 'arpu' || metricType === 'cac' || metricType === 'ltv') {
      return formatCurrencyAmount(value || 0);
    }
    if (metricType === 'margin' || metricType === 'churn') {
      return formatPercentageValue(value);
    }
    return value.toString();
  };

  return (
    <div className="bg-white dark:bg-darkBg rounded-2xl shadow border border-gray-100 dark:border-darkBg-border">
      <div className="p-5 border-b border-gray-100 dark:border-darkBg-border flex flex-wrap justify-between items-center">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Key Business Metrics</h2>

        <div className="flex space-x-2 bg-gray-100 dark:bg-gray-700 p-1 rounded-md">
          <button
            className={`px-3 py-1 text-xs font-medium rounded ${activeMetricType === 'growth' ? 'bg-white shadow' : 'text-gray-500 hover:text-gray-900 dark:text-gray-200'
              }`}
            onClick={() => setActiveMetricType('growth')}
          >
            Growth Metrics
          </button>
          <button
            className={`px-3 py-1 text-xs font-medium rounded ${activeMetricType === 'financial' ? 'bg-white shadow' : 'text-gray-500 hover:text-gray-900 dark:text-gray-200'
              }`}
            onClick={() => setActiveMetricType('financial')}
          >
            Financial Metrics
          </button>
          <button
            className={`px-3 py-1 text-xs font-medium rounded ${activeMetricType === 'customer' ? 'bg-white shadow' : 'text-gray-500 hover:text-gray-900 dark:text-gray-200'
              }`}
            onClick={() => setActiveMetricType('customer')}
          >
            Customer Metrics
          </button>
        </div>
      </div>

      <div className="p-5">
        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {activeMetrics.map((metric, index) => (
            <div
              key={index}
              className={`p-4 rounded-2xl border ${metric.className}`}
            >
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">{metric.title}</p>
                <div className={`p-1 rounded-full bg-white bg-opacity-50`}>
                  {metric.icon}
                </div>
              </div>
              <p className="text-xl font-bold mt-2">{metric.value}</p>
              <p className="text-xs mt-1">
                {/* {metric.subtext && <span className="block mb-1">{metric.subtext}</span>}
                {metric.change} */}
              </p>
            </div>
          ))}
        </div>

        {/* Historical Metrics Chart */}
        {/* <div className="mt-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-medium text-gray-900">Metrics History</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(metricDisplayNames).map(([key, name]) => (
                <button
                  key={key}
                  className={`px-2 py-1 text-xs font-medium rounded ${selectedHistoryMetric === key
                    ? 'bg-indigo-100 text-indigo-800'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  onClick={() => setSelectedHistoryMetric(key)}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>

          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={businessKeyMetrics?.historicalData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.6} vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12 }}
                  tickFormatter={
                    (value) => {
                      return formatValue(value, selectedHistoryMetric);
                    }
                  }
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  name={metricDisplayNames[selectedHistoryMetric]}
                  type="monotone"
                  dataKey={selectedHistoryMetric}
                  stroke={
                    selectedHistoryMetric === 'arpu' || selectedHistoryMetric === 'ltv' ? '#4f46e5' :
                      selectedHistoryMetric === 'cac' ? '#0ea5e9' :
                        selectedHistoryMetric === 'margin' ? '#10b981' :
                          '#ef4444'
                  }
                  strokeWidth={3}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default KeyBusinessMetricsPanel;
